/**
 * pages/wishlist-done.tsx
 *
 * /wishlist/done — completed items archive grouped by vibe.
 * Read-only view with full attribution per Section 12.2 (done archive variant).
 */
import { useNavigate } from 'react-router-dom'
import type { WishlistVibe } from '@just-us/shared'
import { useWishlistDone } from '../hooks/use-wishlist'
import BottomNav from '../components/bottom-nav'
import { VIBE_META } from '../components/wishlist/vibe-chip'

const ALL_VIBES: WishlistVibe[] = ['wander', 'watch', 'taste', 'weekend', 'try', 'together']

const formatDate = (ts: number) =>
  new Date(ts * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

const DoneItemSkeleton = () => (
  <div className="py-3 border-b border-[#F9E0E8] flex flex-col gap-1">
    <div className="h-[15px] bg-[#F9E0E8] rounded-full w-2/3 animate-shimmer" />
    <div className="h-[11px] bg-[#F9E0E8] rounded-full w-1/2 animate-shimmer" />
  </div>
)

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function WishlistDonePage() {
  const navigate = useNavigate()
  const { data, isLoading } = useWishlistDone()

  const doneItems = data?.data?.items ?? []

  // Group by vibe — only include vibes that have done items
  const grouped = ALL_VIBES.map((vibe) => ({
    vibe,
    items: doneItems.filter((i) => i.vibe === vibe),
  })).filter((g) => g.items.length > 0)

  return (
    <div className="min-h-screen bg-[#FAF8F4] flex flex-col max-w-[430px] mx-auto relative">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#FAF8F4] pt-[env(safe-area-inset-top)] border-b border-[#F9E0E8]">
        <div className="flex items-center justify-between px-5 h-[56px]">
          <button
            type="button"
            onClick={() => navigate('/wishlist')}
            className="w-[36px] h-[36px] rounded-full bg-[#FBEAF0] flex items-center justify-center active:scale-90 transition-all duration-150 touch-manipulation"
            aria-label="Back"
          >
            <span className="text-[16px]">←</span>
          </button>
          <span className="font-['Playfair_Display'] text-[18px] font-medium text-[#2C1810]">
            Done together 🫙
          </span>
          <div className="w-[36px]" />
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 overflow-y-auto px-5 pt-4 pb-[88px]">
        {isLoading ? (
          <div>
            {Array.from({ length: 5 }).map((_, i) => (
              <DoneItemSkeleton key={i} />
            ))}
          </div>
        ) : grouped.length === 0 ? (
          <div className="flex flex-col items-center justify-center pt-16 gap-3 animate-fade-in">
            <div className="text-[52px] animate-float">🫙</div>
            <p className="font-['Playfair_Display'] text-[18px] font-medium text-[#2C1810]">
              Nothing done yet
            </p>
            <p className="font-['DM_Sans'] text-[13px] text-[#B89AA2] text-center px-8">
              When you check something off your wishlist together, it lands here.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-8">
            {/* Summary count */}
            <p className="font-['DM_Sans'] text-[13px] text-[#B89AA2]">
              {doneItems.length} thing{doneItems.length === 1 ? '' : 's'} you've done together
            </p>

            {grouped.map(({ vibe, items }) => {
              const meta = VIBE_META[vibe]
              return (
                <div key={vibe}>
                  {/* Vibe section header */}
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-[18px]">{meta.icon}</span>
                    <span
                      className="font-['DM_Sans'] text-[13px] font-semibold"
                      style={{ color: meta.text }}
                    >
                      {meta.label}
                    </span>
                    <span className="font-['DM_Sans'] text-[11px] text-[#B89AA2]">
                      · {items.length}
                    </span>
                  </div>

                  {/* Done items list */}
                  <div
                    className="rounded-[16px] border px-4"
                    style={{ borderColor: meta.border, background: meta.bg }}
                  >
                    {items.map((item) => (
                      <div
                        key={item.id}
                        className="py-3 border-b last:border-b-0"
                        style={{ borderColor: meta.border }}
                      >
                        <p className="font-['DM_Sans'] text-[15px] text-[#2C1810] line-through truncate">
                          {item.title}
                        </p>
                        {/* Done archive attribution per Section 12.2 */}
                        <p className="font-['DM_Sans'] text-[11px] text-[#B89AA2] mt-1">
                          Added by {item.isAddedByMe ? 'you' : item.addedByNickname}
                          {' · '}
                          Done by {item.isDoneByMe ? 'you' : item.doneByNickname}
                          {item.doneAt && (
                            <>
                              {' · '}
                              {formatDate(item.doneAt)}
                            </>
                          )}
                        </p>
                        {/* Note (if any) */}
                        {item.note && (
                          <p className="font-['DM_Sans'] text-[12px] text-[#7A5A62] mt-1 truncate">
                            {item.note}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </main>

      <BottomNav />
    </div>
  )
}
